import type { SelectedTextData } from '~/utils/copy-with-source'
import type { Marking, MarkingColor } from '~/utils/markings'
import { datadogRum } from '@datadog/browser-rum'
import { consola } from 'consola'
import { DEFAULT_NOTE_MARKING_COLOR, MARKING_MESSAGES } from '~/constants/markings'
import { getToast } from '~/utils/toast'
import { TocStateKey } from '~/composables/book/useTocState'
import { captureSectionPath } from '~/utils/markings/section-path'
import { captureMarkingSelection, MarkingSelectionError } from '~/utils/markings/capture'
import { clearBrowserTextSelection, detectCitationFromSelection } from '~/utils/copy-with-source'
import {
  closestMarkElement,
  computeMarkingPosition,
  dispatchNoteIndicatorOpen,
  findSectionInfo,
  getCurrentPageUrl,
  isMarkingColor,
  markingDomRange,
  newMarkingId,
  recolorMarkingDom,
  serializeMarkingHtml,
  unwrapMarking,
  wrapRangeWithMark,
} from '~/utils/markings'

interface UseSelectionMarkingsOptions {
  containerKeys: MaybeRefOrGetter<readonly ContainerKey[]>
  capturedRange: Ref<Range | null>
  selectionRanges: Readonly<Ref<Range[]>>
  selectedTextData: Readonly<Ref<SelectedTextData | null>>
  tooltipCoords: Readonly<Ref<{ x: number, y: number }>>
  hideTooltip: () => void
  showTooltipForRange: (range: Range, mouseX: number, mouseY: number) => boolean
}

export function useSelectionMarkings(options: UseSelectionMarkingsOptions) {
  const { capturedRange, selectionRanges, selectedTextData, tooltipCoords, hideTooltip, showTooltipForRange } = options

  // Marking that owns the current selection — further color clicks toggle/swap.
  const activeMarkingId = ref<string | null>(null)
  const activeMarkingColor = ref<MarkingColor | null>(null)

  const { addMarking, allMarkings, removeMarking, updateMarking } = useMarkings()
  const containers = inject(diKeys.containers)
  const toast = getToast()
  const bookToc = inject(TocStateKey, null)

  // New/cleared capturedRange revokes ownership. Sync flush so the reset
  // can't lag behind a subsequent click.
  watch(capturedRange, () => {
    activeMarkingId.value = null
    activeMarkingColor.value = null
  }, { flush: 'sync' })

  function markableRange(): Range | null {
    return capturedRange.value ?? selectionRanges.value?.[0] ?? null
  }

  async function buildMarking(range: Range, color: MarkingColor): Promise<Marking | null> {
    const citationContext = detectCitationFromSelection(range)
    const { title: sectionTitle, anchor: sectionAnchor } = findSectionInfo(range.startContainer, citationContext)
    const roots = toValue(options.containerKeys).map(key => toValue(containers?.[key]))

    const pageUrl = getCurrentPageUrl()
    const pageHash = window.location.hash || null
    const position = computeMarkingPosition(range, roots)
    const sectionPath = captureSectionPath(range, bookToc?.tocItems.value ?? [])
    let capturedSource

    try {
      capturedSource = await captureMarkingSelection(range, sectionAnchor)
    }
    catch (error) {
      if (error instanceof MarkingSelectionError) {
        toast.error(error.message)
        return null
      }

      // Anything else is a capture bug, not a selection the user can fix by
      // choosing differently — don't disguise it as one.
      consola.error('Marking capture failed', error)
      toast.error(MARKING_MESSAGES.selectionFailed)
      return null
    }

    const html = serializeMarkingHtml(range)
    return {
      schemaVersion: 2,
      ...capturedSource,
      source: { ...capturedSource.source, sectionPath },
      id: newMarkingId(),
      color,
      html: html || undefined,
      pageUrl,
      pageHash,
      citation: citationContext,
      sectionTitle,
      sectionAnchor: sectionAnchor || null,
      note: null,
      createdAt: new Date().toISOString(),
      position,
    }
  }

  /**
   * Wraps the selection in the DOM before the write lands, so the highlight
   * appears on click rather than a round trip later, then undoes the wrap if
   * the store refuses it. Returns null in that case, so callers never proceed
   * against a marking with no server row.
   */
  async function createMarkingForSelection(color: MarkingColor): Promise<Marking | null> {
    const range = markableRange()

    if (!range) {
      return null
    }

    const marking = await buildMarking(range, color)

    if (!marking || range !== markableRange()) {
      return null
    }

    if (range.startContainer.isConnected && !markingDomRange(marking.id)) {
      wrapRangeWithMark(range, { id: marking.id, color })
    }

    activeMarkingId.value = marking.id
    activeMarkingColor.value = color
    clearBrowserTextSelection()

    if (!await addMarking(marking)) {
      unwrapMarking(marking.id)
      activeMarkingId.value = null
      activeMarkingColor.value = null
      return null
    }

    return marking
  }

  async function handleColorClick(color: MarkingColor) {
    if (!selectedTextData.value) {
      return
    }

    // Removal and recolor act on the DOM before the round trip resolves, and a
    // refused write rolls the store back. Rehydration re-places a marking the
    // store holds with no <mark> of its own, which covers the removal; a colour
    // left behind it never sees, so that one is undone below.
    const activeId = activeMarkingId.value

    if (activeId && activeMarkingColor.value === color) {
      unwrapMarking(activeId)
      // capturedRange now points at text nodes the original wrap mutated —
      // force a re-select rather than marking against a stale range.
      hideTooltip()
      await removeMarking(activeId)
      return
    }

    // Swap color by retagging the <mark> elements in place — unwrap + re-wrap
    // is impossible because the Range references detached text nodes.
    if (activeId) {
      const previousColor = activeMarkingColor.value
      recolorMarkingDom(activeId, color)
      activeMarkingColor.value = color

      const saved = await updateMarking(activeId, { color })

      if (!saved && previousColor) {
        recolorMarkingDom(activeId, previousColor)
        activeMarkingColor.value = previousColor
      }

      return
    }

    await createMarkingForSelection(color)
  }

  async function handleNoteClick(event: MouseEvent) {
    event.stopPropagation()

    if (!selectedTextData.value) {
      return
    }

    let marking: Marking | undefined = activeMarkingId.value
      ? allMarkings.value.find(candidate => candidate.id === activeMarkingId.value)
      : undefined

    if (!marking) {
      // Abort on refused write so the note tooltip never opens against a
      // phantom marking with no server row.
      const created = await createMarkingForSelection(DEFAULT_NOTE_MARKING_COLOR)

      if (!created) {
        return
      }

      marking = created
    }

    // tooltipCoords are page-coords; the note tooltip expects clientX/Y.
    const scrollX = window.scrollX || document.documentElement.scrollLeft || 0
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0
    dispatchNoteIndicatorOpen({
      id: marking.id,
      clientX: tooltipCoords.value.x - scrollX,
      clientY: tooltipCoords.value.y - scrollY,
      // Inline so the listener needn't wait for the optimistic mutation to
      // land in the shared store.
      marking,
    })
    hideTooltip()
  }

  function isInsideContainers(node: Node): boolean {
    for (const key of toValue(options.containerKeys)) {
      const root = toValue(containers?.[key])

      if (root?.contains(node)) {
        return true
      }
    }

    return false
  }

  // Clicking marked text reopens the tooltip for that marking so its color
  // can be swapped/removed or a note added — note editing itself only opens
  // via the tooltip's «Notat» action.
  useEventListener(document, 'click', (event: MouseEvent) => {
    const targetEl = event.target instanceof Element ? event.target : null
    const mark = closestMarkElement(targetEl)

    if (!mark || !isInsideContainers(mark)) {
      return
    }

    // A drag-selection ending on a mark belongs to the selection flow.
    const selection = window.getSelection()

    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      return
    }

    const id = mark.dataset.markingId
    const marking = id ? allMarkings.value.find(candidate => candidate.id === id) : undefined

    if (!marking) {
      return
    }

    // A marking with a note opens the note window directly.
    if (marking.note?.trim()) {
      dispatchNoteIndicatorOpen({ id: marking.id, clientX: event.clientX, clientY: event.clientY })
      hideTooltip()
      return
    }

    const range = markingDomRange(marking.id)

    if (!range || !showTooltipForRange(range, event.clientX, event.clientY)) {
      return
    }

    // After showTooltipForRange replaced capturedRange (which sync-resets the
    // ownership), adopt the clicked marking so color clicks swap/toggle it.
    activeMarkingId.value = marking.id
    activeMarkingColor.value = isMarkingColor(marking.color) ? marking.color : null
  })

  let isSavingSelection = false

  async function saveSelection(action: () => Promise<void>) {
    if (isSavingSelection) {
      return
    }

    isSavingSelection = true

    try {
      await action()
    }
    catch {
      datadogRum.addError(new Error('Selected marking could not be rendered'), { operation: 'selection' })
      toast.error(MARKING_MESSAGES.renderFailed)
    }
    finally {
      isSavingSelection = false
    }
  }

  return {
    activeMarkingColor,
    handleColorClick: (color: MarkingColor) => saveSelection(() => handleColorClick(color)),
    handleNoteClick: (event: MouseEvent) => saveSelection(() => handleNoteClick(event)),
  }
}
